<template>
  <side-panel-inner
    class="idea"
    :on-close="`/collaboration/question/${idea.question_id}`"
  >
    <div
      slot="header"
      v-if="nextIdea"
      class="c-panel__header-title u-hide-mobile"
    >
      <p>
        <a href="#" @click.prevent="goToNextIdea" class="next-article">
          <span>Next idea:</span>
          <br />
          {{ nextIdea.title }}
        </a>
      </p>
    </div>

    <h2 slot="header" class="c-panel__header-title" v-else>{{ idea.title }}</h2>
    <loading v-if="loading" />
    <div
      :class="{
        'negative-margin':
          questionOwner && !actionedStatus.includes(idea.status),
      }"
      v-else
    >
      <div class="full-width">
        <action-bar
          title="Action Required"
          v-if="questionOwner && !actionedStatus.includes(idea.status)"
        >
          <template slot="content">
            <p>
              Please take action on this idea. If you feel information is
              lacking you can ask the owner to review their idea, or simply
              decline it. Alternatively, please indicated if an idea has been
              implemented.
            </p>
            <inline-list withPadding>
              <li>
                <base-button @click="onReview" inline iconBefore="search"
                  >Please review</base-button
                >
              </li>
              <li>
                <base-button
                  successInvert
                  inline
                  @click="onImplement"
                  iconBefore="tick"
                  >Implement</base-button
                >
              </li>
              <li>
                <base-button
                  dangerInvert
                  inline
                  @click="onDecline"
                  iconBefore="cross"
                  >Decline</base-button
                >
              </li>
            </inline-list>
          </template>
        </action-bar>
        <action-bar title="Idea in review" v-else-if="idea.status === 'review'">
          <template slot="content">
            <p>
              This idea is currently in review and is likely to change in the
              future.
            </p>
          </template>
        </action-bar>
        <div class="idea__article">
          <header class="idea__article__header">
            <div
              class="idea__article__header__votes"
              :class="{ smaller: idea.vote_count >= 100 }"
              v-if="allowVotes"
            >
              <em>{{ idea.vote_count > 99 ? '99+' : idea.vote_count }}</em
              >votes
            </div>
            <div class="idea__article__header__content">
              <h4>
                Idea
                <router-link
                  class="edit-link"
                  :to="{
                    name: 'IdeaEdit',
                    params: { questionId: questionId, ideaId: ideaId },
                  }"
                  v-if="ideaOwner"
                  >Edit</router-link
                >&nbsp;
                <base-button
                  asLink
                  @click="onDeleteClick"
                  v-if="questionOwner || ideaOwner || canDelete"
                  class="ta-right"
                  >Delete</base-button
                >
              </h4>
              <h1>{{ idea.title }}</h1>
            </div>
          </header>

          <main>
            <content-expand :content="idea" type="idea" />
          </main>

          <p>
            <base-button
              :warning="true"
              :square="true"
              iconBefore="thumbsup"
              :iconBeforeFill="false"
              @click="onVote"
              v-if="allowVotes && UserHasPermission && questionOpen"
              :class="{ disabled: !userCanVote }"
              mobile-full-width
            >
              <span v-if="userCanVote">Vote for this idea</span>
              <span v-else>Remove vote</span>
            </base-button>
          </p>

          <p v-if="idea.status == 'implemented'">
            <inline-label type="success"
              >This idea has been implemented</inline-label
            >
          </p>
          <p v-if="idea.status == 'declined'">
            <inline-label type="danger"
              >This idea has been declined</inline-label
            >
          </p>

          <footer>
            <div class="idea__author">
              <router-link
                :to="{ name: 'UserProfile', params: { id: idea.user.id } }"
                class="author"
              >
                <user-avatar :user="idea.user" />
              </router-link>
              <router-link
                :to="{ name: 'UserProfile', params: { id: idea.user.id } }"
                class="author"
                >{{ idea.user.name }}</router-link
              >
            </div>
            <meta-row
              :object="idea"
              :show-votes="false"
              :show-response-count="false"
            />
          </footer>
        </div>
      </div>

      <section class="idea__comments full-width">
        <add-comment
          :idea="idea"
          @submit="onCommentAdd"
          v-if="UserCanComment && questionOpen"
        />
        <p v-else>Comments for this idea have been closed</p>
        <hr />

        <comment-list>
          <transition-group name="fade" tag="ol" slot="comments">
            <li
              v-for="(comment, index) in reverseComments"
              :key="comment.id"
              :class="{ hide: index >= 5 }"
              :id="`comment_${comment.id}`"
            >
              <comment-container
                :comment="comment"
                @reply="onReplyClick"
                @submit="onReplyAdd"
                @delete="onCommentDelete"
                :replyTo="replyTo"
                :canComment="UserCanComment"
                :questionOpen="questionOpen"
              ></comment-container>
            </li>
          </transition-group>
        </comment-list>
        <base-button
          :asLink="true"
          v-if="reverseComments.length > 5 && !hideShowMore"
          @click="showAll"
          >Show {{ reverseComments.length - 5 }} more comments</base-button
        >
      </section>
    </div>
    <modal
      v-if="showIdeaReviewModal"
      class="ta-left"
      show-close-button
      @close="showIdeaReviewModal = false"
    >
      <h1 slot="header">Review idea</h1>
      <form method="post" v-on:submit.prevent="onAddReview">
        <p>Please give feedback on the idea.</p>
        <form-field
          full-width
          name="reason"
          type="textarea"
          v-model="reviewFeedback"
          required
          v-validate="{ required: true }"
          data-vv-as="reason"
          :error="errors.first('reason')"
        ></form-field>

        <base-button type="submit" @click="onAddReview"
          >Submit feedback</base-button
        >
      </form>
    </modal>
  </side-panel-inner>
</template>

<script>
const MetaRow = () => import('@/components/MetaRow');
import { InlineList } from '@/components/Lists';
import { InlineLabel } from '@/components/elements';
import { ActionBar, ContentExpand } from '@/components/Containers';
import { mapState, mapGetters } from 'vuex';
import {
  CommentContainer,
  AddComment,
  CommentList,
} from '@/components/Comments';
const BaseButton = () => import('@/components/BaseButton');
const UserAvatar = () => import('@/components/UserAvatar');
import UserHasPermission from '@/mixins/UserHasPermission';

export default {
  name: 'question-create',

  data() {
    return {
      showValidationMessage: false,
      loading: true,
      comment: '',
      showFull: this.shrinkDescription,
      hideShowMore: false,
      actionedStatus: ['implemented', 'declined', 'closed', 'review'],
      showIdeaReviewModal: false,
      reviewFeedback: null,
      nextIdea: null,
    };
  },

  /**
   * TODO:
   * What we need to do is determine whether we are posting to a main comment or a reply
   * This should be similar in nature to the pages, so look at how that is done
   */
  methods: {
    followQuestion() {
      // Once submitted, we need to get the user to auto-follow the question
      this.$store
        .dispatch('Questions/follow', this.questionId)
        .then(resp => {
          this.$store.dispatch(
            'notifications/create',
            {
              body: `You are now following this question`,
              type: 'success',
            },
            { root: true }
          );
          this.question.user_favourite = true;
        })
        .catch(err => {
          this.$store.dispatch(
            'notifications/create',
            {
              body: `There was a problem - ${err}`,
              type: 'error',
            },
            { root: true }
          );
        });
    },
    onAddReview() {
      const vm = this;
      this.$validator.validate().then(result => {
        if (!result) {
          vm.validationFailed = true;
        } else {
          vm.validationFailed = false;
          // send a review
          vm.$store.dispatch('Ideas/review', this.reviewFeedback).then(resp => {
            vm.reviewFeedback = null;
            vm.showIdeaReviewModal = false;
            vm.$store.dispatch(
              'notifications/create',
              {
                body: `Your feedback has been sent`,
                type: 'success',
              },
              { root: true }
            );
            vm.$store.dispatch('CLOSE_PANEL', 'CollaborationQuestion');
          });
          // this.$emit("update", this.el);
        }
      });
    },
    onDeleteClick() {
      const vm = this;
      this.$store.dispatch('createDialog', {
        confirm: () => {
          this.$store
            .dispatch('Ideas/delete', {
              questionId: this.questionId,
              ideaId: this.ideaId,
            })
            .then(resp => {
              vm.$router.push({
                to: `/collaboration/question/${this.questionId}`,
              });
            })
            .catch(err => {});
        },
      });
    },
    onVote() {
      //if the user has already voted
      let endpoint = 'Ideas/vote';
      let action = 'added';
      if (!this.userCanVote) {
        endpoint = 'Ideas/unvote';
        action = 'removed';
      }

      // TODO: When we vote should we just append to local state, rather then re-read from api?
      this.$store
        .dispatch(endpoint, {
          questionId: this.questionId,
          ideaId: this.ideaId,
        })
        .then(resp => {
          this.$store.dispatch(
            'notifications/create',
            {
              body: `Your vote has been ${action}`,
              type: 'success',
            },
            { root: true }
          );
          this.followQuestion();
        })
        .catch(err => {
          this.$store.dispatch(
            'notifications/create',
            {
              title: 'Error!',
              body: `Your vote could not be ${action} - ${err}`,
              type: 'danger',
            },
            { root: true }
          );
        });
    },
    goToNextIdea() {
      const vm = this;
      const { nextIdea } = this;
      const { question_id, id } = nextIdea;
      vm.loading = true;

      //reset idea and comments
      this.$store.dispatch('Ideas/resetState');
      this.$store.dispatch('Comments/resetState');

      this.$store
        .dispatch('Ideas/read', { questionId: question_id, ideaId: id })
        .then(resp => {
          this.$store.dispatch('Comments/list', {
            questionId: question_id,
            ideaId: id,
            nested: true,
          });
          vm.$router.push({
            name: 'CollaborationIdea',
            params: {
              questionId: question_id,
              ideaId: id,
            },
          });
          vm.loading = false;
        });
    },
    /**
     * Replies need to do the same, but just add a parent_id
     */
    onReplyAdd(resp) {
      return this.onCommentAdd(resp, this.replyTo);
    },
    onCommentAdd(resp, parentId = null) {
      const vm = this;
      const comment = {
        ...this.$route.params,
        title: resp,
        parentId: parentId,
      };
      this.$store
        .dispatch('Comments/addComment', comment)
        .then(resp => {
          vm.$store.dispatch(
            'notifications/create',
            {
              body: `Your comment was successfully added`,
              type: 'success',
            },
            { root: true }
          );
          //increment the idea count
          this.idea.comment_count += 1;
          this.followQuestion();
        })
        .catch(err => {
          vm.$store.dispatch(
            'notifications/create',
            {
              title: 'Error!',
              body: `Your comment could not be added`,
              type: 'danger',
            },
            { root: true }
          );
          console.log({ ...err });
        });
    },

    onCommentDelete(comment) {
      const payload = {
        questionId: this.idea.question_id,
        ideaId: this.idea.id,
        commentId: comment.id,
      };
      this.$store.dispatch('createDialog', {
        confirm: () => {
          this.$store.dispatch('Comments/deleteComment', payload);
        },
      });
    },

    onReview() {
      this.showIdeaReviewModal = true;
    },

    onImplement() {
      // confirm first
      this.$store.dispatch('createDialog', {
        confirm: () => {
          this.$store
            .dispatch('Ideas/implement', {
              questionId: this.questionId,
              ideaId: this.ideaId,
            })
            .then(resp => {
              this.$store.dispatch(
                'notifications/create',
                {
                  body: `The idea has been marked as implemented`,
                  type: 'success',
                },
                { root: true }
              );
            })
            .catch(err => {
              console.log({ ...err });
            });
        },
      });
    },

    onDecline() {
      // confirm first
      this.$store.dispatch('createDialog', {
        confirm: () => {
          this.$store
            .dispatch('Ideas/decline', {
              questionId: this.questionId,
              ideaId: this.ideaId,
            })
            .then(resp => {
              this.$store.dispatch(
                'notifications/create',
                {
                  body: `The idea has been marked as declined`,
                  type: 'success',
                },
                { root: true }
              );
            })
            .catch(err => {
              console.log({ ...err });
            });
        },
      });
    },
    onReplyClick(id) {
      this.$store.dispatch('Comments/setOpenComment', id);
    },
  },

  computed: {
    ...mapState('Ideas', ['idea', 'ideas']),
    ...mapState('Questions', ['question']),
    ...mapState('Comments', ['comments', 'replyTo']),
    ...mapGetters('Comments', ['reverseComments']),
    canDelete() {
      return this.userHasPermission('delete_ideas');
    },
    userCanApprove() {
      return this.userHasPermission;
    },
    currentUserId() {
      return this.$store.state.auth.currentUser.id;
    },
    questionOpen() {
      if (this.actionedStatus.includes(this.idea.status)) {
        return false;
      }
      if (this.question.status == 'closed') {
        return false;
      }
      return (
        this.$dayjs(this.question.end_date).format('YYYY-MM-DD') >
        this.$dayjs().format('YYYY-MM-DD')
      );
    },
    questionOwner() {
      if (
        !this.$store.state.auth.currentUser ||
        !this.$store.state.Questions.question.created_user
      ) {
        return false;
      }
      return (
        this.$store.state.auth.currentUser.id ===
        this.$store.state.Questions.question.created_user.id
      );
    },
    ideaOwner() {
      return (
        this.$store.state.auth.currentUser.id ===
        this.$store.state.Ideas.idea.created_user.id
      );
    },
    questionId() {
      return this.$route.params.questionId;
    },
    ideaId() {
      return this.$route.params.ideaId;
    },

    allowVotes() {
      return this.$store.state.Questions.question.voting ? true : false;
    },
    userCanVote() {
      if (!this.idea.votes) {
        return true;
      }
      const currentUser = this.$store.state.auth.currentUser.id;
      const userVotes = this.idea.votes.filter(vote => vote.id === currentUser);
      return userVotes.length >= 1 ? false : true;
    },
    UserHasPermission() {
      return this.userHasPermission('vote_on_ideas');
    },
    UserCanComment() {
      return this.userHasPermission('comment_on_ideas');
    },
  },
  components: {
    MetaRow,
    AddComment,
    BaseButton,
    CommentContainer,
    CommentList,
    InlineList,
    InlineLabel,
    ActionBar,
    ContentExpand,
    UserAvatar,
  },

  mixins: [UserHasPermission],

  mounted() {
    // this.$store.dispatch("Ideas/list", {questionId: this.questionId, isFlat: true});

    this.$store.dispatch('Comments/list', {
      questionId: this.questionId,
      ideaId: this.ideaId,
      nested: true,
    });

    //next link here, computed seems a bit too update-y
    if (this.ideas.length > 0) {
      const currentIndex = this.ideas.findIndex(
        item => item.id === this.idea.id
      );
      if (currentIndex >= 0 && currentIndex < this.ideas.length) {
        this.nextIdea = this.ideas[currentIndex + 1];
      }
    }
  },

  updated() {
    //next link here, computed seems a bit too update-y
    if (this.ideas.length > 0) {
      const currentIndex = this.ideas.findIndex(
        item => item.id === this.idea.id
      );
      if (currentIndex >= 0 && currentIndex < this.ideas.length) {
        this.nextIdea = this.ideas[currentIndex + 1];
      }
    }
  },

  created() {
    this.$store
      .dispatch('Ideas/read', {
        questionId: this.questionId,
        ideaId: this.ideaId,
      })
      .then(resp => {
        const currentUserId = this.$store.state.auth.currentUser.id;
        const ideaUser = this.idea.created_user.id;
        if (
          this.idea.status === 'review' &&
          currentUserId === ideaUser
        ) {
          // if the question is in review, forward - if we are the owner
          this.$router.push({
            path: `/collaboration/question/${this.questionId}/idea/${this.ideaId}/edit`,
          });
        }
        this.loading = false;
      });
  },
};
</script>

<style lang="scss" scoped>
// TODO: So much style...
.idea {
  background: white;
  width: 100%;
  // margin-top: -100px;
  //   padding-top: 100px;

  .next-article {
    color: $colour-nhs-mid-grey;
    font-weight: lighter;
    text-decoration: none;
    position: relative;
    display: block;
    cursor: pointer;
    font-size: 18px;
    line-height: 1.2;
    font-family: $font-frutiger-light;
    padding-right: 20px;

    span {
      color: $colour-nhs-core-blue;
      font-weight: bold;
      font-size: 16px;
      line-height: 1.1;
      font-family: $font-frutiger-bold;
    }

    &:after {
      position: absolute;
      right: 0;
      top: 50%;
      margin-top: -14px;
      display: block;
      content: ' ';
      background-image: url('/assets/arrow.svg');
      background-size: 28px 28px;
      height: 28px;
      width: 28px;
      transition: all ease-in-out 0.2s;
    }

    &:hover {
      &:after {
        right: -5px;
      }
    }
  }

  &__article {
    padding: 30px 10px;
    background-color: tint($colour-nhs-orange, 6);
    // @media screen and (min-width: 1024px) {
    background-size: 102px 192px;
    @media (min-width: 768px) {
      background-size: auto auto;
      padding: 50px 20px 20px 130px;
    }
    background-image: url(/assets/idea.svg);
    background-position: 0 50px;
    background-attachment: scroll;
    // }
    width: 100%;
    box-sizing: border-box;

    position: relative;

    & &__header {
      display: flex;
      margin-bottom: 1rem;
      padding: 50px 20px 20px 130px;

      @media (min-width: 768px) {
        padding: 0;
      }
      &__votes {
        position: absolute;
        top: -5px;
        left: 50%;
        transform: translateX(-50%);
        background: white;
        text-align: center;
        padding: 15px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.08);
        border-radius: 4px;
        font-size: 16px;

        @media (min-width: 768px) {
          position: relative;
          transform: none;
          left: auto;
          top: auto;
          width: 80px;
          height: 80px;
          padding: 5px 10px;
        }
        box-sizing: border-box;
        margin-right: 1rem;

        color: $colour-nhs-orange;

        em {
          font-style: normal;
          margin-right: 5px;
          @media (min-width: 768px) {
            margin-right: 0;
            display: block;
            font-weight: bold;
            font-size: 44px;
          }
        }

        &.smaller {
          @media (min-width: 768px) {
            em {
              font-size: 35px;
            }
          }
        }
      }

      &__content {
        h4 {
          color: $colour-nhs-orange;
          text-transform: uppercase;
          margin-top: 0;
          font-size: 1rem;
        }

        h1 {
          font-weight: normal;
          font-family: $font-frutiger-light;
          font-size: 20px;
        }
      }
    }

    &__article {
      line-height: 1.25;
    }
  }

  &__comments {
    background: white;
    padding: 3vmin 5vmin;
  }

  &__author {
    display: flex;
    padding: 10px 0;
    margin-top: 10px;
    border-bottom: 1px solid
      rgba(red(#f28521), green(#f28521), blue(#f28521), 0.1);
    // justify-content: space-between;
    align-items: center;

    a {
      text-decoration: none;
    }

    @media (min-width: 768px) {
      display: none;
    }

    .c-avatar {
      max-width: 42px;
      margin-right: 20px;
    }

    address {
      font-style: normal;
    }
  }

  footer {
    margin-top: 20px;
    padding-bottom: 20px;
    border-top: 1px solid rgba(red(#f28521), green(#f28521), blue(#f28521), 0.1);

    .c-meta-row {
      padding-top: 11px;
    }
  }
}

.full-width {
  margin-left: -10px;
  min-width: calc(100% + 20px);
  box-sizing: border-box;
  max-width: 100vw;

  @media screen and (min-width: 1024px) {
    margin-left: -3em;
    min-width: calc(100% + 6em);
  }

  @media screen and (min-width: 1170px) {
    margin-left: -6em;
    min-width: calc(100% + 12em);
  }
}

hr {
  margin: 1em 0;
}

.hide {
  display: none;
}

.disabled {
  opacity: 0.5;
  cursor: default;
}

.edit-link {
  margin-left: 10px;
  text-decoration: none;
  text-transform: none;

  &:hover {
    color: darken($colour-nhs-core-blue, 10%);
  }
}

.negative-margin {
  @media screen and (min-width: 1024px) {
    margin-top: -40px;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>

<style lang="scss">
.c-panel__content {
  padding-top: 90px !important ;
}
</style>
